// 양식 업로드 Job의 **종료 문구** 한 곳 — 토스트와 결과 화면이 같은 문장을 쓴다.
//
// 문구를 두 벌로 두면 "45개 완료"와 "44개 완료"가 화면과 알림에서 갈리는 종류의 어긋남이
// 생긴다. 순수 함수로 빼 두면 그 어긋남이 구조적으로 불가능하고, 카운트 규칙(취소를 실패로
// 세지 않는다 등)도 한 자리에서 검증된다.

import type { FormImportJobResult, FormImportProgress } from '../../lib/api';

/**
 * **성공 판정의 단일 축** — "열 수 있는 초안이 생겼는가"를 `result` 유무로만 본다.
 *
 * 종전에는 이 판정이 두 갈래였다: 문구·색은 `completedCount`로, Job 수명주기
 * (useFormImportJob.onComplete)는 `result` 유무로. 두 축이 갈리면 counts가 비어 온 성공 Job이
 * **초록 결과 화면 + 빨간 "분석에 실패했습니다" 토스트**로 갈라진다. `result`를 정본으로 삼는
 * 이유는 그것이 화면이 실제로 걸려 있는 값이기 때문이다 — 결과 화면은 `result` 없이는 그릴 수
 * 없고, 타입 주석도 "전건 실패·취소로 성공이 0건이면 result 자체가 없다"로 계약을 못박는다.
 * counts는 **문구의 숫자**(N개 중 M개)에만 쓴다.
 *
 * 타입 술어로 둬서 호출부(훅)가 이 함수로 좁히게 한다 — 축을 공유한다는 사실이 타입에 박힌다.
 */
export function formImportProducedDraft(
  progress: FormImportProgress | null | undefined,
): progress is FormImportProgress & { result: FormImportJobResult } {
  return !!progress?.result;
}

/** 배치 전체 항목 수 — 취소로 착수하지 못한 항목까지 포함해야 "N개 중"이 사용자가 고른 수와 맞는다. */
export function formImportTotalCount(progress: FormImportProgress | null | undefined): number {
  if (!progress) return 0;
  return (
    (progress.completedCount ?? 0) + (progress.failedCount ?? 0) + (progress.cancelledCount ?? 0)
  );
}

/**
 * 종료 알림 문구 — 개별 시트마다 토스트를 쏘지 않고(47시트면 47번) **배치 전체가 끝났을 때
 * 1회만** 이 문장을 쓴다. 취소로 끝난 경우도 마찬가지로 1회다.
 *
 * 단건(항목 1개)은 종전 문구를 그대로 유지한다 — 이미지 한 장을 올린 사람에게
 * "1개 중 1개 변환 완료"는 군더더기다.
 */
export function describeFormImportOutcome(progress: FormImportProgress | null | undefined): string {
  const completed = progress?.completedCount ?? 0;
  const failed = progress?.failedCount ?? 0;
  const cancelled = progress?.cancelledCount ?? 0;
  const total = formImportTotalCount(progress);

  // 단건은 counts가 아니라 초안 유무(formImportProducedDraft)로 가른다 — 문구가 "결과를
  // 확인하세요"라고 안내하는 대상이 바로 그 result다.
  const producedDraft = formImportProducedDraft(progress);
  if (total <= 1) {
    if (progress?.status === 'cancelled') {
      return producedDraft
        ? '변환을 취소했습니다 — 만들어진 초안은 상단에서 확인하세요.'
        : '변환을 취소했습니다.';
    }
    if (producedDraft) return '양식 변환이 완료됐습니다 — 상단에서 결과를 확인하세요.';
    return progress?.error ?? '양식 분석에 실패했습니다.';
  }

  const parts = [`${total}개 중 ${completed}개 변환 완료`];
  if (failed > 0) parts.push(`${failed}건 실패`);
  if (cancelled > 0) parts.push(`${cancelled}건 취소`);
  return parts.join(' · ');
}

/**
 * 종료 알림의 색 — 하나라도 초안이 생겼으면 성공으로 본다.
 *
 * 부분 실패를 error로 띄우면 45개를 만들어 낸 배치가 빨간 실패로 읽힌다. 실패한 시트의
 * 사유는 결과 목록(sheets)에 남아 있으므로 알림까지 경고색일 필요는 없다. 반대로 초안이
 * 하나도 없으면(전건 실패·즉시 취소) 성공색은 거짓말이다.
 *
 * 판정 축은 화면(결과 phase)과 **같은 값**이다 — formImportProducedDraft 참조.
 */
export function formImportOutcomeToastType(
  progress: FormImportProgress | null | undefined,
): 'success' | 'error' {
  return formImportProducedDraft(progress) ? 'success' : 'error';
}
